// @flow
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { isValidURL } from 'utils/validators';
import bgImageEmergency from 'assets/images/grid_emergency.svg';
import bgImageSavings from 'assets/images/grid_pig02.svg';
import bgImageEducation from 'assets/images/grid_grad.svg';
import bgImageRainy from 'assets/images/grid_rainy.svg';
import bgImageCustom from 'assets/images/grid_badge.svg';
import selectorUnchecked from 'assets/images/selector-unchecked.png';
import selectorChecked from 'assets/images/selector-checked.png';
import bgImageFunFund from 'assets/images/grid_fun.svg';
import ErrorLabel from 'components/shared/ErrorLabel/ErrorLabel';
import { sendAnalyticsEvent } from 'utils/analyticsWrapper';
import { Title, SmallTitle } from 'components/shared/CustomizeGoalcardTitle/Title';
import { getUserPossesivePronoun } from 'utils/string';
import {
  Container,
  SupportItem,
  Checkbox,
  Label,
  SpecificSupportContainer,
  UnespecificSupportContainer,
  AmountBoxContainer,
  AmountBox,
  GoalContainer,
  GoalImageContainer,
  GoalImage,
  GoalInfoContainer,
  GoalInfoName,
  GoalInfoSavedAmount,
  GoalInfoAmountContainer,
  GoalTotalContainer,
  GoalTotalLabel,
  AmountInputMasked,
  GoalInfoAmountInputMasked,
  SelectorImage,
} from './Styles';

type GoalcardSupportOptionsProps = {
  userId: string,
  receiverGoalId: string,
  receiverName: string,
  receiverNameWithPosessive: string,
  gender: string,
  goals: Array<Object>,
  setAmountCallback(amount: number): void,
  amountByGoalCallback(inputValues: Array<Object>): void
};

const GoalcardSupportOptions = (props: GoalcardSupportOptionsProps) => {
  const { t } = useTranslation();

  const {
    register, trigger, setValue, errors,
  } = useFormContext(); // retrieve all hook methods

  const [pageState, updatePageState]: [any, ((any => any) | any) => void] = useState({});

  const {
    userId,
    receiverName,
    receiverNameWithPosessive,
    receiverGoalId,
    gender,
    goals,
    setAmountCallback,
    amountByGoalCallback,
    goalcardPayload,
    supportType,
  } = props;

  const receiverPossesivePronoun = getUserPossesivePronoun(gender);

  useEffect(() => {
    updatePageState({
      supportType: (supportType || (userId ? 'specific' : 'nonspecific')),
      amount: goalcardPayload?.amount || undefined,
      inputValues: goalcardPayload?.valuesByGoal || [],
    });

    register({ name: 'goalcardAmount' }, { required: true, min: 1, max: 200 });
  }, []);

  useEffect(() => { setAmountCallback(pageState.amount || undefined); }, [pageState.amount]);

  const updateAmountAndCallback = (amount) => {
    updatePageState({ ...pageState, amount });
    setAmountCallback((amount || undefined));
    setValue('goalcardAmount', amount || undefined, { shouldValidate: false });
  };

  const updateAmountByGoalCallback = (inputValues) => {
    amountByGoalCallback(inputValues);
  };

  useEffect(() => {
    if (pageState.inputValues && pageState.inputValues.length > 0) {
      let amount = 0;
      pageState.inputValues.forEach((data) => {
        if (data.value) {
          amount += parseFloat(data.value);
        }
      });

      updateAmountAndCallback(amount);
    }
    updateAmountByGoalCallback(pageState.inputValues);
  }, [pageState.inputValues]);

  useEffect(() => {
    if (goals && pageState.inputValues && pageState.inputValues.length === 0 && pageState.supportType === 'specific' && !receiverGoalId) {
      const goalsInputsInitValue = goals.map((data) => ({
        goalId: data.rid,
        value: 0,
      }));
      updatePageState({ ...pageState, inputValues: goalsInputsInitValue });
    } else if (pageState.supportType === 'nonspecific') {
      updatePageState({ ...pageState, inputValues: [] });
    }
  }, [goals, pageState.supportType]);

  const handleSupportTypeChange = (event) => {
    updatePageState({
      ...pageState,
      supportType: event.target.value,
      inputValues: [],
      amount: undefined,
    });

    setValue('goalcardAmount', 0, { shouldValidate: false });
    sendAnalyticsEvent('Customize Goalcard', `customize_goalcard_supporttype_${event.target.value}`);
  };

  const handleAmountFromButton = (amountValue) => {
    sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_amount_frombutton');

    const amount = amountValue ? parseFloat(amountValue) : undefined;
    updatePageState({
      ...pageState,
      amount,
    });

    setValue('goalcardAmount', amount, { shouldValidate: true });
  };

  const handleAmountFromInput = (amountValue) => {
    sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_amount_frominput');

    const amount = amountValue ? parseFloat(amountValue) : 0;
    updatePageState({
      ...pageState,
      amount,
    });

    setValue('goalcardAmount', amount, { shouldValidate: true });
  };

  const handleAmount = (amountValue) => {
    sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_amount_frominputorbutton');

    const amount = amountValue ? parseFloat(amountValue) : 0;
    updatePageState({
      ...pageState,
      amount,
    });

    setValue('goalcardAmount', amount, { shouldValidate: true });
  };

  const showErrors = () => (
    <>
      {errors.goalcardAmount?.type === 'required' && <ErrorLabel text={t('customize_validation_goalcardamount_notcompleted_title')} />}
      {errors.goalcardAmount?.type === 'min' && <ErrorLabel text={t('customize_validation_goalcardamount_minamount_title')} />}
      {errors.goalcardAmount?.type === 'max' && <ErrorLabel text={t('customize_validation_goalcardamount_maxamount_title')} />}
    </>
  );

  const handleGoalAmounts = (goalId, value) => {
    sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_amount_fromgoals');

    let currentInput = pageState.inputValues.find((x) => x.goalId === goalId);

    if (!currentInput) {
      currentInput = {
        goalId,
        value,
      };

      updatePageState({
        ...pageState,
        inputValues: pageState.inputValues.concat(currentInput),
      });
    } else {
      currentInput.value = value || 0;

      updatePageState({
        ...pageState,
        inputValues: pageState.inputValues
          .map((inputs) => (inputs.goalId === currentInput.goalId ? currentInput : inputs)),
      });
    }
  };

  const getGoalIcon = (type, url) => {
    if (isValidURL(url)) {
      return url;
    }

    switch (type) {
      case 1: return bgImageSavings;
      case 2: return bgImageEducation;
      case 3: return bgImageFunFund;
      case 4: return bgImageCustom;
      case 5: return bgImageEmergency;
      case 6: return bgImageRainy;
      default: return bgImageCustom;
    }
  };

  const decimalMask = {
    num: {
      mask: Number,
      radix: '.',
      mapToRadix: [','],
      unmask: true,
      signed: false,
      normalizeZeros: true,
      padFractionalZeros: true,
      max: 1000,
      min: 0,
    },
  };

  const RenderUnspecificSupportContainer = () => {
    const { amount } = pageState;

    return (
      <UnespecificSupportContainer>
        <Title>{t('customize_nonspecificpreset_title')}</Title>
        <AmountBoxContainer>
          <AmountBox
            checked={amount === 25}
            onClick={() => handleAmountFromButton(25)}
            data-testid="25"
          >
            $25
          </AmountBox>
          <AmountBox
            checked={amount === 50}
            onClick={() => handleAmountFromButton(50)}
            data-testid="50"
          >
            $50
          </AmountBox>
          <AmountBox
            checked={amount === 75}
            onClick={() => handleAmountFromButton(75)}
            data-testid="75"
          >
            $75
          </AmountBox>
          <AmountBox
            checked={amount === 100}
            onClick={() => handleAmountFromButton(100)}
            data-testid="100"
          >
            $100
          </AmountBox>
          <AmountBox
            checked={amount === 125}
            onClick={() => handleAmountFromButton(125)}
            data-testid="125"
          >
            $125
          </AmountBox>
        </AmountBoxContainer>
        <SmallTitle>
          {t('customize_nonspecificcustom_title')}
        </SmallTitle>
        <AmountInputMasked
          data-testid="inputGoalcardAmount"
          mask="$num"
          blocks={decimalMask}
          value={pageState.amount ? pageState.amount.toString() : ''}
          unmask
          placeholder="$0.00"
          onAccept={(value) => handleAmountFromInput(value)}
        />
        {showErrors()}
      </UnespecificSupportContainer>
    );
  };

  const getGoalAmountValue = (goalId) => {
    let result = '';

    if (pageState.inputValues) {
      const goal = pageState.inputValues.find((g) => g.goalId === goalId);

      if (goal) {
        result = goal.value.toString();
      }
    }
    return result;
  };

  const renderGoalAmountInformation = (goal) => {
    let result = null;

    const hasFixedAmount = goal.amount && goal.amount > 0;

    if (goal.visibilityType === 0) {
      if (hasFixedAmount) {
        result = t('customize_goalsaved_fixed_title', { amount: goal.publicAchievedAmount, amountFixed: goal.amount });
      } else {
        result = t('customize_goalsaved_title', { amount: goal.publicAchievedAmount });
      }
    } else if (goal.visibilityType === 1) {
      if (hasFixedAmount) {
        const percentage = (goal.publicAchievedAmount * 100) / goal.amount;
        result = t('customize_goalsaved_percentage_title', { percentage });
      }
    }

    return result;
  };

  const RenderGoals = () => {
    if (goals) {
      return goals.map((goal) => (
        <GoalContainer key={goal.rid}>
          <GoalImageContainer>
            <GoalImage src={getGoalIcon(goal.type, goal.wishImage)} />
          </GoalImageContainer>
          <GoalInfoContainer>
            <GoalInfoName>
              {goal.prettyName}
            </GoalInfoName>
            <GoalInfoSavedAmount>
              {renderGoalAmountInformation(goal)}
            </GoalInfoSavedAmount>
          </GoalInfoContainer>
          <GoalInfoAmountContainer>
            <GoalInfoAmountInputMasked
              mask="$num"
              blocks={decimalMask}
              unmask
              value={getGoalAmountValue(goal.rid)}
              placeholder="$0.00"
              onAccept={(value) => handleGoalAmounts(goal.rid, value)}
            />
          </GoalInfoAmountContainer>
        </GoalContainer>
      ));
    }

    return null;
  };

  const RenderSpecifiedSupportContainer = () => (
    <>
      <SpecificSupportContainer>
        <Title marginBottom={10}>
          {t('customize_specificgoal_title')}
        </Title>
        {RenderGoals()}
        <GoalTotalContainer>
          <GoalTotalLabel>Total</GoalTotalLabel>
          <GoalTotalLabel>
            {pageState.amount ? `$ ${pageState.amount.toFixed(2)}` : '$0.00'}
          </GoalTotalLabel>
        </GoalTotalContainer>
      </SpecificSupportContainer>
      {showErrors()}
    </>
  );

  const RenderSupportContainer = () => {
    if (!userId || pageState.supportType === 'nonspecific' || receiverGoalId) {
      return RenderUnspecificSupportContainer();
    }

    return RenderSpecifiedSupportContainer();
  };

  const RenderSupportOptionsRadio = () => {
    if (userId && goals && !receiverGoalId) {
      return (
        <>
          <Title>
            {t('customize_support_title')}
          </Title>
          <SupportItem>
            <Label>
              <SelectorImage src={pageState.supportType === 'specific' ? selectorChecked : selectorUnchecked} alt="selector" />
              <Checkbox type="radio" name="options" value="specific" checked={pageState.supportType === 'specific'} onChange={handleSupportTypeChange} />
              {t('customize_support_option1', { receiverNameWithPosessive })}
            </Label>
          </SupportItem>
          <SupportItem>
            <Label>
              <SelectorImage src={pageState.supportType === 'nonspecific' ? selectorChecked : selectorUnchecked} alt="selector" />
              <Checkbox type="radio" name="options" value="nonspecific" checked={pageState.supportType === 'nonspecific'} onChange={handleSupportTypeChange} />
              {t('customize_support_option2', { receiverName, receiverPossesivePronoun })}
            </Label>
          </SupportItem>
        </>
      );
    }

    return null;
  };

  return (
    <>
      <Container>
        {RenderSupportOptionsRadio()}
        {RenderSupportContainer()}
      </Container>
    </>
  );
};

// Map Props
const mapStateToProps = (state) => ({
  isProcessing: state.customize.isProcessing,
  goals: state.customize.goals,
  goalcardPayload: state.purchase.goalcardPayload,
});

export default connect(
  mapStateToProps,
)(GoalcardSupportOptions);
